/**
 * The one place HTTP happens.
 *
 * A short-lived access token goes out in an Authorization header, held in
 * memory only (see ./tokenStore). The refresh token that renews it is an
 * HttpOnly cookie scoped to /v1/auth, so it is never attached to a patient
 * request and no script here can read it.
 */
import { ApiError, type ApiErrorDetail } from '@/api/errors'
import { getAccessToken, millisUntilExpiry, setAccessToken } from '@/api/tokenStore'

const BASE = import.meta.env.VITE_API_BASE_URL ?? ''

export interface RefreshedSession {
  access_token: string
  expires_in: number
  user: unknown
}

/**
 * One refresh at a time, ever.
 *
 * Refresh tokens rotate, and presenting a rotated one is how the server detects
 * theft — it revokes the whole chain. A screen opening fires several queries at
 * once, so they expire together and 401 together; letting each start its own
 * refresh would send the same token three times and get the doctor forcibly
 * signed out mid-consultation. Every caller awaits the same promise instead.
 */
let refreshInFlight: Promise<RefreshedSession | null> | null = null

type RefreshedListener = (session: RefreshedSession) => void
const refreshedListeners = new Set<RefreshedListener>()

/** Notified when a background refresh produces a new session. */
export function onSessionRefreshed(listener: RefreshedListener): () => void {
  refreshedListeners.add(listener)
  return () => refreshedListeners.delete(listener)
}

/**
 * Renews the access token from the refresh cookie. Null if it could not be.
 *
 * Returns the payload rather than a bare boolean so a caller can apply it
 * directly instead of waiting to be called back — which matters on page load,
 * where the callback is registered by an effect that StrictMode unmounts and
 * remounts, and a response arriving inside that window would reach nobody.
 */
export function refreshSession(): Promise<RefreshedSession | null> {
  refreshInFlight ??= (async () => {
    try {
      // Plain fetch, not `request()`: a 401 from the refresh endpoint itself
      // would come straight back through the retry below and recurse.
      const response = await fetch(buildUrl('/v1/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
      })
      if (!response.ok) return null

      const session = (await response.json()) as RefreshedSession
      setAccessToken(session.access_token, session.expires_in)
      for (const listener of refreshedListeners) listener(session)
      return session
    } catch {
      // Offline, or the server is down. Not an auth failure — do not sign a
      // doctor out over a dropped connection.
      return null
    } finally {
      refreshInFlight = null
    }
  })()
  return refreshInFlight
}

/** True when the token is gone or about to be, so it is renewed first. */
export function needsRefreshBeforeRequest(): boolean {
  return getAccessToken() !== null && millisUntilExpiry() <= 0
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | undefined | null>
  /** Sent on every visit write. A doctor on clinic wifi will double-tap save. */
  idempotencyKey?: string
  /** The version loaded, so a concurrent edit is a 409 rather than silent loss. */
  ifMatch?: number
  signal?: AbortSignal
}

/** Listeners fire when the server says the session is gone, so the app can
 *  clear local state rather than leaving a shared machine half-signed-in. */
type UnauthenticatedListener = (error: ApiError) => void
const unauthenticatedListeners = new Set<UnauthenticatedListener>()

export function onUnauthenticated(listener: UnauthenticatedListener): () => void {
  unauthenticatedListeners.add(listener)
  return () => unauthenticatedListeners.delete(listener)
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }
  return url.toString()
}

export interface Envelope<T> {
  data: T
  /** From the ETag. Needed to send If-Match on the next write, so a concurrent
   *  edit on another device becomes a 409 instead of a silent overwrite. */
  version: number | null
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  return (await requestWithVersion<T>(path, options)).data
}

export async function requestWithVersion<T>(
  path: string,
  options: RequestOptions = {},
): Promise<Envelope<T>> {
  const { method = 'GET', body, query, idempotencyKey, ifMatch, signal } = options

  // Only these two are excluded from renewal. `/v1/auth/me` is an ordinary
  // authenticated read and MUST be able to trigger one — it is the call that
  // restores a session on page load, where the access token only ever lived in
  // the memory of the page that has just been thrown away.
  //   - /v1/auth/refresh: a 401 here means the refresh token itself is dead.
  //   - /v1/auth/session: a 401 here is a wrong password, not an expired token.
  const skipRenewal = path === '/v1/auth/refresh' || path === '/v1/auth/session'

  // Already expired: renew first rather than spending a request to be told so.
  if (!skipRenewal && needsRefreshBeforeRequest()) await refreshSession()

  function buildInit(): RequestInit {
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey
    if (ifMatch !== undefined) headers['If-Match'] = `"${ifMatch}"`
    const token = getAccessToken()
    if (token) headers.Authorization = `Bearer ${token}`

    // Built conditionally: exactOptionalPropertyTypes means an explicit
    // `undefined` is not the same as an absent key.
    const init: RequestInit = {
      method,
      headers,
      // Carries the refresh cookie on /v1/auth calls. It is path-scoped, so it
      // is not sent with anything else.
      credentials: 'include',
    }
    if (body !== undefined) init.body = JSON.stringify(body)
    if (signal) init.signal = signal
    return init
  }

  async function send(): Promise<Response> {
    try {
      return await fetch(buildUrl(path, query), buildInit())
    } catch (cause) {
      // A cancelled request is not a failure; let TanStack see the AbortError.
      if (signal?.aborted) throw cause
      // Only claim "offline" when the browser agrees. Everything else is an
      // honest "could not reach the server".
      throw navigator.onLine === false ? ApiError.offline() : ApiError.network()
    }
  }

  let response = await send()

  // The token expired between the check above and the server reading it, or it
  // was rejected outright. Renew once, then replay the request exactly as sent
  // — the idempotency key travels with it, so a replayed write is still one
  // write. A second 401 means the session is genuinely over.
  if (response.status === 401 && !skipRenewal && (await refreshSession())) {
    response = await send()
  }

  if (response.status === 204) return { data: undefined as T, version: null }

  const text = await response.text()
  const payload: unknown = text ? safeParse(text) : null

  if (!response.ok) {
    const error = toApiError(response.status, payload)
    if (error.isUnauthenticated) {
      setAccessToken(null)
      for (const listener of unauthenticatedListeners) listener(error)
    }
    throw error
  }

  return { data: payload as T, version: parseVersion(response.headers.get('ETag')) }
}

function parseVersion(etag: string | null): number | null {
  if (!etag) return null
  const parsed = Number(etag.replace(/^W\//, '').replace(/"/g, ''))
  return Number.isFinite(parsed) ? parsed : null
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function toApiError(status: number, payload: unknown): ApiError {
  const envelope = (payload as { error?: Record<string, unknown> } | null)?.error
  if (!envelope) {
    return new ApiError(status, 'internal_error', 'Something went wrong.')
  }
  return new ApiError(
    status,
    String(envelope.code ?? 'internal_error'),
    String(envelope.message ?? 'Something went wrong.'),
    (envelope.details as ApiErrorDetail[] | undefined) ?? [],
    (envelope.correlation_id as string | null | undefined) ?? null,
  )
}
